import _ from 'lodash';
import moment from 'moment';

import { getPrettyHoursOrDaysSince, pluralizeCount } from './formatting';
import { formatPrice, isBrandMismatch } from './url_data_agent_live_matches_helpers';

export const LINKS_TO_CHECK_TABS = [
  { question: 'same_product', label: 'Same product?' },
  { question: 'new_product', label: 'New product?' },
  { question: 'what_is_it', label: 'What is it?' },
  { question: 'skip', label: 'Skip?' },
  { question: 'likely_wrong', label: 'Likely wrong' }
];

// The matcher's own vocabulary, which the page always puts in plain words; the tests check every visible string against it.
export const INTERNAL_TERMS = /\b(?:apm|verdicts?|confidence|thresholds?|gauntlet|adjudicators?|rulesets?|tiers?|siblings?|evaluations?|holds?|holding|held|stems?)\b/i;

/* ────────────────────────────────────────────────────────────────
 * The card's question, our suggestion and its buttons
 * ─────────────────────────────────────────────────────────────── */

export const getLinkQuestion = link => {
  if (link.isUndone) return 'What is it instead?';
  if (link.cardType === 'same_item') return 'Are these the same item?';
  if (link.cardType === 'alert') return link.applied?.action === 'create' ? 'Did we add this right?' : 'Did we link this right?';
  if (link.question === 'same_product') return link.pick ? 'Is this the same product?' : 'Which one is it?';
  if (link.question === 'new_product') return 'Should we add this as a new product?';
  if (link.question === 'what_is_it') return "We couldn't read this page. What is it?";
  const listing = getListingState(link);
  if (listing === 'sold') return 'This listing has sold. Skip it?';
  if (listing === 'out_of_stock') return 'This listing is out of stock. Skip it?';
  return "This doesn't look like a product page. Skip it?";
};

// Every button on the card in display order; the one marked primary is the dark button.
export const getLinkCardActions = link => {
  // Keep both is recorded on the run that added the product, so a backlog pair has none.
  if (link.cardType === 'same_item') {
    return _.compact([ACTIONS.merge, link.alert && ACTIONS.keep_both, ACTIONS.help, { ...ACTIONS.not_now, display: 'Not sure' }]);
  }
  if (link.cardType === 'alert') return getAlertActions(link);
  if (link.question === 'same_product') {
    return link.pick
      ? [{ ...ACTIONS.link, primary: true }, ACTIONS.search, ACTIONS.create, ACTIONS.skip, ACTIONS.help, ACTIONS.not_now]
      : [{ ...ACTIONS.search, display: 'None of these: search' }, ACTIONS.create, ACTIONS.skip, ACTIONS.help, ACTIONS.not_now];
  }
  if (link.question === 'new_product') {
    return [{ ...ACTIONS.create, shortcut: 'A', display: 'Add it', primary: true }, ACTIONS.search, ACTIONS.skip, ACTIONS.help, ACTIONS.not_now];
  }
  if (link.question === 'what_is_it') return [{ ...ACTIONS.open_page, primary: true }, ACTIONS.create, ACTIONS.skip, ACTIONS.help, ACTIONS.not_now];
  return getSkipCardActions(link);
};

// One plain sentence: what we think and why we didn't do it ourselves.
export const getSuggestionSentence = link => {
  if (link.isUndone) return 'We undid our link. Pick the right product, add it as new, or skip it.';
  if (['alert', 'same_item'].includes(link.cardType)) return getAlertSentences(link).join(' ');
  if (link.question === 'skip' && getListingState(link)) return getDefaultSuggestion(link);
  return (HOLD_REASON_SUGGESTIONS[link.holdReason] || getDefaultSuggestion)(link);
};

// Keyed by every reason the matcher stamps when it leaves a link to people (applySkipReason, or the triage stop).
export const HOLD_REASON_SUGGESTIONS = {
  below_threshold: link => getLeadSentence(link, `we weren't sure enough to ${SUGGESTION_VERBS[link.question]} ourselves`),
  catalog_product_needs_review: link => getLeadSentence(link, 'our pick is missing details in the catalog (photo, brand or category)'),
  product_hidden: link => getLeadSentence(link, 'our pick is hidden from shoppers'),
  retailer_code_conflict: link => getLeadSentence(link, "the page and our pick's listing carry different product codes from this retailer"),
  gender_conflict: link => getLeadSentence(link, "the page is a men's item and our pick is a women's product"),
  fit_conflict: link => getLeadSentence(link, "the page is a Petite or Tall fit and our pick's title doesn't name it"),
  duplicate_colorway_records: link => getLeadSentence(link, 'the catalog has this product under more than one colourway record'),
  source_redirected_to_homepage: link => getLeadSentence(link, "the link now opens the shop's front page"),
  visual_comparison_failed: link => getLeadSentence(link, "our photo check didn't finish"),
  url_stem_conflict: () => 'The link points at a product from another brand.',
  url_taken_by_product: () => 'Another product took this link while we were checking it.',
  creation_url_listed_by_product: () => 'A catalog product already lists this link.',
  suppressed_pair: () => "Links from this site aren't matched to our pick's brand.",
  preowned_item_on_new_goods_product: () => 'This is a pre-owned listing, and our pick is a new item.',
  resale_listing_on_other_listing_product: () => "This resale listing is one item, and our pick is another listing's product.",
  preowned_source: () => 'This shop sells pre-owned items, and our pick is a new item.',
  creation_preowned_channel: link => getLeadSentence(link, 'the catalog would mark it as pre-owned'),
  source_condition_unverified: () => "We couldn't tell if it's new or pre-owned.",
  source_condition_conflict: () => "We couldn't tell if it's new or pre-owned.",
  vinted_listing: () => "The catalog doesn't take Vinted listings, so skip it unless the catalog already has this exact listing.",
  creation_identity_incomplete: link => getLeadSentence(link, getMissingIdentityClause(link)),
  brand_possible_duplicate: link => getLeadSentence(link, getPossibleBrandClause(link)),
  creation_brand_conflict: link => getLeadSentence(link, getPossibleBrandClause(link)),
  catalog_brand_attribution_unverified: () => "We couldn't verify which catalog brand this shop's product label belongs to.",
  creation_image_needs_review: link => getLeadSentence(link, "we couldn't get a photo from the page"),
  duplicate_product: link => getLeadSentence(link, getSameNameClause(link)),
  duplicate_product_unresolved: link => getLeadSentence(link, getSameNameClause(link)),
  creation_possible_duplicate: link => getLeadSentence(link, getPossibleDuplicateClause(link)),
  creation_same_product_confirmed: link => getLeadSentence(link, 'we found a catalog product that looks like the same item in another size, set or material'),
  invalid_product: link => getLeadSentence(link, "it's missing details every product needs"),
  creation_source_missing: link => getLeadSentence(link, "the link isn't a regular web address"),
  brand_creation_busy: link => getLeadSentence(link, 'we were adding a brand for another link at the same moment'),
  proposal_changed: () => 'Our suggestion changed while we were checking it.',
  reserved_by_human: () => 'A teammate had this link open when we finished checking it.',
  creation_awaiting_requester: () => 'A teammate confirmed this is a new product and will add it.',
  source_changed: () => 'The page changed after we checked it.',
  sold_listing_without_stored_page: () => 'The listing was sold when we read it, but we had no saved copy of the page to confirm that.',
  source_blocked: () => "The site blocked us, so we couldn't see the page.",
  ignored_domain: () => "We don't read pages from this site.",
  crawl_domain_skip: () => "We don't read pages from this site.",
  crawl_domain_dormant: () => "We don't read pages from this site.",
  domain_blocked_by_policy: () => "We don't read pages from this site.",
  nonproduct_evidence_unverified: () => "We think it isn't a product page, but we couldn't prove it.",
  not_a_product_page: () => "We think it isn't a product page.",
  url_data_not_found: () => "We couldn't find this link's details.",
  dismissed_permanently: () => 'Someone already skipped this link.',
  ...Object.fromEntries(
    [
      'creation_incomplete_run',
      'creation_needs_review',
      'creation_search_incomplete',
      'evidence_needs_review',
      'incomplete_run',
      'execution_retries_exhausted',
      'execution_retry_pending',
      'duplicate_product_recovery',
      'apply_error',
      'agent_user_missing'
    ].map(reason => [reason, link => getChecksDidNotFinish(link)])
  ),
  ...Object.fromEntries(
    [
      'verdict_not_attach',
      'missing_product_id',
      'creation_listing_sold_out',
      'lane_paused',
      'review_first',
      'released_by_pause',
      'proposal_expired',
      'sent_to_human',
      'corrected_by_reviewer',
      'superseded_by_human',
      'urldata_no_longer_eligible',
      'run_undone',
      'action_create_product',
      'action_dismiss',
      'action_escalate',
      'action_no_usable_match'
    ].map(reason => [reason, link => getDefaultSuggestion(link)])
  )
};

// Sold and out-of-stock one-of-a-kind listings; null for everything else, in-stock listings included.
export const getListingState = link => {
  const { isOneOfAKindShop, availability } = link.page || {};
  if (!isOneOfAKindShop && link.holdReason !== 'creation_listing_sold_out') return null;
  if (availability === 'in_stock') return null;
  if (availability === 'out_of_stock') return 'out_of_stock';
  return availability === 'sold' || link.holdReason === 'creation_listing_sold_out' ? 'sold' : null;
};

const ACTIONS = {
  link: { key: 'link', shortcut: 'A', display: "Yes, it's this one" },
  search: { key: 'search', shortcut: 'C', display: 'Search' },
  create: { key: 'create', shortcut: 'N', display: 'Add as new' },
  skip: { key: 'skip', shortcut: 'S', display: 'Skip' },
  help: { key: 'help', shortcut: 'H', display: 'Ask for help' },
  not_now: { key: 'not_now', shortcut: '→', display: 'Not now' },
  open_page: { key: 'open_page', shortcut: 'O', display: 'Open the page' },
  alert_right: { key: 'alert_right', shortcut: 'A', display: 'Looks right' },
  alert_repoint: { key: 'alert_repoint', shortcut: 'C', display: "It's another product" },
  undo_link: { key: 'undo_link', shortcut: 'X', display: 'Undo our link' },
  undo_add: { key: 'undo_add', shortcut: 'N', display: 'Undo and add as new' },
  undo_skip: { key: 'undo_skip', shortcut: 'S', display: 'Undo and skip' },
  merge: { key: 'merge', shortcut: 'M', display: 'Same item: merge' },
  keep_both: { key: 'keep_both', shortcut: 'K', display: 'Different: keep both' }
};

// An out-of-stock listing can come back, so it gets no dark button.
const getSkipCardActions = link => {
  const listing = getListingState(link);
  if (listing === 'out_of_stock') return [ACTIONS.create, ACTIONS.skip, ACTIONS.search, ACTIONS.help, ACTIONS.not_now];
  const sold = listing === 'sold';
  return [
    {
      ...ACTIONS.skip,
      shortcut: 'A',
      display: sold ? 'Skip: sold' : 'Skip: not a product page',
      dismissReason: sold ? 'sold_one_of_a_kind' : 'not_a_product_page',
      primary: true
    },
    { ...ACTIONS.create, display: sold ? 'Still for sale? Add it' : "It's one product: add it" },
    ACTIONS.search,
    ACTIONS.help,
    ACTIONS.not_now
  ];
};

const getAlertActions = link => {
  const isAdd = link.applied?.action === 'create';
  const check = link.alert?.check;
  return _.compact([
    check === 'media_file' && { ...ACTIONS.undo_skip, display: 'Undo and skip it', dismissReason: 'not_a_product_page', primary: true },
    ACTIONS.alert_right,
    isAdd ? { ...ACTIONS.alert_repoint, display: "It's already in the catalog" } : ACTIONS.alert_repoint,
    !isAdd && ACTIONS.undo_link,
    !isAdd && { ...ACTIONS.undo_add, display: check === 'preowned_on_new_goods' ? 'Undo and add its own product' : ACTIONS.undo_add.display },
    !isAdd && check !== 'media_file' && ACTIONS.undo_skip,
    isAdd && ACTIONS.help,
    ACTIONS.not_now
  ]);
};

const SUGGESTION_LEADS = { same_product: "We think it's this one", new_product: "We'd add this", skip: "We think it isn't a product page" };
const SUGGESTION_VERBS = { same_product: 'link it', new_product: 'do it', skip: 'skip it' };

const getSuggestionLead = link => (link.question === 'same_product' && !link.pick ? null : SUGGESTION_LEADS[link.question]);

const getLeadSentence = (link, clause) => {
  const lead = getSuggestionLead(link);
  return lead ? `${lead}, but ${clause}.` : getDefaultSuggestion(link);
};

const getChecksDidNotFinish = link =>
  getSuggestionLead(link) ? getLeadSentence(link, "our checks didn't finish") : "Our checks didn't finish on this one.";

const getDefaultSuggestion = link => {
  if (link.question === 'what_is_it') return "We couldn't tell what product the page shows.";
  if (link.question === 'skip') {
    const listing = getListingState(link);
    if (listing === 'sold') return 'The shop shows it as sold, and each listing there is a single item.';
    if (listing === 'out_of_stock') return 'The shop shows it as out of stock, and each listing there is a single item.';
    return "We think it isn't a product page.";
  }
  if (link.verdict === 'new_variant') return "We think it's another version of a product in the catalog.";
  if (link.question === 'same_product') return link.pick ? "We think it's this one." : "We found a few close matches and couldn't choose.";
  return "We'd add this as a new product.";
};

const getMissingIdentityClause = ({ proposal }) => {
  if (proposal?.brand && proposal.brandInCatalog === false) return `the brand “${proposal.brand}” isn't in our catalog under that name`;
  if (proposal && !proposal.category) return "we're missing its category";
  return "we're missing its name, brand or category";
};

const getPossibleBrandClause = ({ proposal }) => {
  const name = proposal?.brandSuggestions?.[0]?.name;
  return name ? `this brand might already be in the catalog as “${name}”` : 'this brand might already be in the catalog under another spelling';
};

const getSameNameClause = ({ similarInCatalog }) => {
  const title = similarInCatalog?.[0]?.title;
  return title ? `the catalog already has a product with a very close name: “${title}”` : 'a product with this name is already in the catalog';
};

const getPossibleDuplicateClause = ({ similarInCatalog }) => {
  const title = similarInCatalog?.[0]?.title;
  return title ? `the catalog may already have it as “${title}”` : 'the catalog may already have it from the same brand';
};

/* ────────────────────────────────────────────────────────────────
 * Likely wrong: what we did on our own that may be wrong
 * ─────────────────────────────────────────────────────────────── */

export const LIKELY_WRONG_CHECKS = {
  borderline_attach: {
    chip: 'Borderline',
    sentence: 'We linked this on our own, but only just. People keep links like this about 6 in 10 times.',
    usualFix: 'Usual fix: another product (C), or it looks right (A)',
    usualUndoReason: 'wrong_product'
  },
  near_duplicate_create: {
    chip: 'Near-duplicate',
    sentence: 'We added this as new, but the catalog already had a very close product from the same brand. About 1 in 3 of these are the same item.',
    usualFix: 'Usual fix: merge (M) or keep both (K)',
    usualUndoReason: null
  },
  retailer_code_conflict: {
    chip: 'Code differs',
    sentence: 'The product we linked has a different product code at this shop. People keep links like this about 1 in 3 times.',
    usualFix: 'Usual fix: another product (C)',
    usualUndoReason: 'wrong_product'
  },
  preowned_on_new_goods: {
    chip: 'Pre-owned on new',
    sentence: 'This is a pre-owned listing, but we linked it to a new item. People keep links like this fewer than 3 in 10 times.',
    usualFix: 'Usual fix: its own pre-owned product (N)',
    usualUndoReason: 'preowned_or_marketplace'
  },
  fit_range: {
    chip: 'Fit differs',
    sentence: link => {
      const fit = getFitDifference(link);
      return fit
        ? `The page is the ${fit} version, and the product we linked isn't. People kept 7 of 24 links like this.`
        : 'The page and the product we linked are for different fits or sizes. People kept 7 of 24 links like this.';
    },
    usualFix: link => {
      const fit = getFitDifference(link);
      return fit ? `Usual fix: the ${fit} product (C)` : 'Usual fix: another product (C)';
    },
    usualUndoReason: 'variant_confusion'
  },
  photo_check_failed: {
    chip: 'Photo check failed',
    sentence: link =>
      link.applied?.action === 'create'
        ? 'Our photo check timed out, and we added it anyway. Adds like this were right 16% of the time.'
        : 'Our photo check timed out, and we linked it anyway. Links like this were right about half the time.',
    usualFix: (link, { canMerge }) => {
      if (link.applied?.action !== 'create') return 'Usual fix: another product (C), or undo our link (X)';
      return canMerge ? "Usual fix: it's already in the catalog (C)" : "Usual fix: it's already in the catalog. Ask for help (H)";
    },
    usualUndoReason: 'wrong_product'
  },
  media_file: {
    chip: 'Image link',
    sentence: 'This link is an image file, not a product page, but we linked it to a product. People skip every link like this.',
    usualFix: 'Usual fix: undo and skip it (S)',
    usualUndoReason: 'wrong_product'
  }
};

// A link that trips two checks shows once, with each check's sentence, the main check first.
export const getAlertSentences = link => {
  if (!link.alert) return _.compact([DUPLICATE_PAIR_SENTENCES[link.pair?.evidence]]);
  return _.uniq([link.alert.check, ...(link.alert.alsoTripped || [])])
    .filter(check => LIKELY_WRONG_CHECKS[check])
    .map(check => fill(LIKELY_WRONG_CHECKS[check].sentence, link));
};

export const getUsualFix = (link, { canMerge = true } = {}) => {
  const check = LIKELY_WRONG_CHECKS[link.alert?.check];
  return check ? fill(check.usualFix, link, { canMerge }) : null;
};

// An add card's C merges, so it is off for a reviewer who can't merge.
export const getAlertSubhead = (link, { hasCloseMatches, canMerge = true }) => {
  if (hasCloseMatches) return 'Other close matches from the same run';
  if (link.applied.action !== 'create') return 'No other close matches in the run. Search with C, or undo our link.';
  return canMerge ? 'No other close matches in the run. Search with C.' : 'No other close matches in the run.';
};

const DUPLICATE_PAIR_SENTENCES = {
  one_page: 'One page points at both of these products.',
  two_shops: 'Same name and brand, listed from two different shops.'
};

const FIT_RANGES = [
  ['Big & Tall', /\bbig\s*(?:&|and)\s*tall\b/i],
  ['Tall', /\btall\b/i],
  ['Petite', /\bpetites?\b/i],
  ['Plus', /\bplus\b/i],
  ['Maternity', /\bmaternity\b/i],
  ["kids'", /\b(?:kids?|girls?|boys?|toddlers?|baby|youth)\b/i]
];

const getFitRange = text => FIT_RANGES.find(([, pattern]) => pattern.test(text || ''))?.[0] || null;

// Names the page's fit only when our product's title doesn't carry the same one; two kids' ranges stay unnamed.
const getFitDifference = link => {
  const pageFit = getFitRange([link.page?.title, link.page?.variant, ...[].concat(link.linkWords || [])].join(' '));
  return pageFit && pageFit !== getFitRange(link.applied?.product?.title) ? pageFit : null;
};

/* ────────────────────────────────────────────────────────────────
 * How long it waited, who shared it
 * ─────────────────────────────────────────────────────────────── */

export const getLinkChips = link =>
  _.compact([
    getCheckChip(link),
    link.isPriority && { key: 'priority', label: 'Priority', title: "A live brand campaign's site, or a link a top creator shared this month" },
    link.sellsFast && { key: 'sells_fast', label: 'Sells fast', title: 'A one-of-a-kind listing that is still for sale' },
    link.applied
      ? { key: 'wait', label: getAppliedLabel(link.applied) }
      : link.waitingSince && { key: 'wait', label: getWaitLabel(link.waitingSince) },
    link.creators?.total > 0 && { key: 'creators', label: getCreatorsLabel(link.creators) }
  ]);

export const getWaitLabel = (waitingSince, now = moment()) => `Waiting ${getPrettyHoursOrDaysSince(waitingSince, now)}`;

export const getOldestWaitingLabel = (oldestWaitingSince, now = moment()) =>
  oldestWaitingSince ? `Oldest waiting: ${getPrettyHoursOrDaysSince(oldestWaitingSince, now)}` : null;

export const getAppliedLabel = ({ action, appliedAt }) =>
  `${action === 'create' ? 'Added' : 'Linked'} on our own ${moment(appliedAt).format('MMM D')}`;

export const getCreatorsLabel = ({ total, top } = {}) => {
  if (!total) return null;
  const shared = `Shared by ${pluralizeCount(total, 'creator')}`;
  return top ? `${shared} · ${pluralizeCount(top, 'top creator')}` : shared;
};

const getCheckChip = link => {
  const label = link.alert ? LIKELY_WRONG_CHECKS[link.alert.check]?.chip : link.cardType === 'same_item' && 'Duplicate backlog';
  return label ? { key: 'check', label } : null;
};

/* ────────────────────────────────────────────────────────────────
 * Differences line: the page against a catalog product
 * ─────────────────────────────────────────────────────────────── */

// Brand, the words one name has and the other doesn't, price, and the page's colour or size when ours names it.
export const getLinkDifferences = ({ page, product } = {}) => {
  if (!page || !product) return [];
  return _.compact([
    getBrandDifference(page, product),
    getNameDifference(page, product),
    getPriceDifference(page, product),
    getVariantMatch(page, product)
  ]);
};

const getBrandDifference = (page, product) => {
  if (!page.brand || !product.brand) return null;
  if (_.toLower(_.trim(page.brand)) === _.toLower(_.trim(product.brand))) return { key: 'brand', status: 'same', text: 'Same brand' };
  if (!isBrandMismatch(page.brand, product.brand)) {
    return { key: 'brand', status: 'close', text: `Brand written differently: “${page.brand}” and “${product.brand}”` };
  }
  return { key: 'brand', status: 'different', text: `Different brand: “${page.brand}” here, “${product.brand}” in our catalog` };
};

const getNameDifference = (page, product) => {
  const brandTokens = new Set([...getWords(page.brand), ...getWords(product.brand)].map(toToken).filter(token => token.length > 1));
  const pageWords = getWords(page.title).filter(word => !brandTokens.has(toToken(word)));
  const ourWords = getWords(product.title).filter(word => !brandTokens.has(toToken(word)));
  if (!pageWords.length || !ourWords.length) return null;
  const pageAdds = getWordsMissingFrom(pageWords, ourWords);
  const oursAdds = getWordsMissingFrom(ourWords, [...pageWords, ...getWords(page.variant)]);
  if (!pageAdds.length && !oursAdds.length) return { key: 'name', status: 'same', text: 'Same name' };
  if (!oursAdds.length) return { key: 'name', status: 'different', text: `The page adds ${quoteRuns(pageAdds)}` };
  if (!pageAdds.length) return { key: 'name', status: 'different', text: `Ours adds ${quoteRuns(oursAdds)}` };
  return { key: 'name', status: 'different', text: `The page says ${quoteRuns(pageAdds)}, ours says ${quoteRuns(oursAdds)}` };
};

const getPriceDifference = (page, product) => {
  const here = formatPrice(page.price, page.currency);
  const ours = formatPrice(product.fallbackPrice, product.fallbackPriceCurrency);
  if (!here || !ours) return null;
  return here === ours
    ? { key: 'price', status: 'same', text: 'Same price' }
    : { key: 'price', status: 'different', text: `${here} here, ${ours} in our catalog` };
};

const getVariantMatch = (page, product) => {
  const variantWords = getWords(page.variant);
  if (!variantWords.length) return null;
  const ourTokens = new Set(getWords(product.title).map(toToken));
  return variantWords.every(word => ourTokens.has(toToken(word)))
    ? { key: 'variant', status: 'same', text: `Same colour or size: ${page.variant}` }
    : null;
};

const FILLER_WORDS = new Set(['a', 'an', 'and', 'by', 'for', 'in', 'of', 'on', 'the', 'with']);

const getWords = text =>
  String(text || '')
    .split(/[\s\-–—/|,·]+/)
    .filter(toToken);

const toToken = word =>
  word
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

// Runs of neighbouring words the other name lacks, dropping runs made only of words like "the" or "with".
const getWordsMissingFrom = (words, otherWords) => {
  const others = new Set(otherWords.map(toToken));
  const runs = words.reduce((all, word, index) => {
    if (others.has(toToken(word))) return all;
    if (index > 0 && !others.has(toToken(words[index - 1]))) all[all.length - 1].push(word);
    else all.push([word]);
    return all;
  }, []);
  return runs.filter(run => run.some(word => !FILLER_WORDS.has(toToken(word)))).map(run => run.join(' '));
};

const quoteRuns = runs => runs.map(run => `“${run}”`).join(', ');

/* ────────────────────────────────────────────────────────────────
 * Skip and undo-our-link sheets
 * ─────────────────────────────────────────────────────────────── */

export const SKIP_REASONS = [
  {
    dismissReason: 'not_a_product_page',
    shortcut: '1',
    display: 'Not a product page',
    detail: 'A home, category, search or sign-in page, a cart, an image file, a link-in-bio page'
  },
  { dismissReason: 'sold_one_of_a_kind', shortcut: '2', display: 'Sold, and one of a kind', detail: 'A second-hand listing that has sold' },
  { dismissReason: 'page_gone', shortcut: '3', display: 'Broken link', detail: 'The page is gone and nothing on the card says what it was' }
];

// "Sold" is offered only on one-of-a-kind shops; the nudges warn and never block.
export const getSkipSheet = link => ({
  title: 'Why skip it?',
  reasons: SKIP_REASONS.map(reason =>
    reason.dismissReason === 'sold_one_of_a_kind' && !link.page?.isOneOfAKindShop
      ? { ...reason, disabled: true, detail: 'Only on one-of-a-kind shops. Sold-out products on regular shops are still matched (96%).' }
      : reason
  ),
  instead: { key: 'search', shortcut: 'C', display: 'Already in the catalog? Link it instead', detail: 'So the creators who shared it get matched' },
  nudge: getSkipNudge(link)
});

// The send-back tags POST /api/UrlDataAgentEvaluations/:id/send-to-human accepts, in plain words.
export const UNDO_LINK_REASONS = [
  { reason: 'wrong_product', display: 'Wrong product' },
  { reason: 'variant_confusion', display: 'Right product, wrong colour or size' },
  { reason: 'wrong_brand', display: 'Wrong brand' },
  { reason: 'preowned_or_marketplace', display: "It's pre-owned or a marketplace listing" },
  { reason: 'insufficient_evidence', display: "Can't tell from the page" }
];

// The check's usual reason comes first, so its key is 1.
export const getUndoLinkSheet = link => {
  const usualReason = LIKELY_WRONG_CHECKS[link.alert?.check]?.usualUndoReason;
  const reasons = _.sortBy(UNDO_LINK_REASONS, ({ reason }) => reason !== usualReason);
  return {
    title: "What's wrong with our link?",
    reasons: reasons.map((option, index) => ({ ...option, shortcut: String(index + 1), usual: option.reason === usualReason })),
    note: 'We undo our link, then you finish the card.'
  };
};

const getSkipNudge = link => {
  const { availability, readable } = link.page || {};
  if (link.domain === 'therealreal.com' && availability === 'out_of_stock') return "This can come back. Only “Sold” means it's gone.";
  if (link.question === 'what_is_it' || readable === false) return "Pages we couldn't read still lead to real products. Open it first (O).";
  if (link.question === 'skip' || getListingState(link) || getLinkShape(link.url) === 'collection') return null;
  const total = link.creators?.total;
  if (total === 1) return "This page shows a product. Skipping leaves 1 creator's link unmatched.";
  return `This page shows a product. Skipping leaves ${total ? `the ${total} creators'` : "its creators'"} links unmatched.`;
};

/* ────────────────────────────────────────────────────────────────
 * Keys and link shapes
 * ─────────────────────────────────────────────────────────────── */

export const LINK_KEYS = [
  { keys: ['A'], does: 'What the dark button says' },
  { keys: ['1', '2', '3'], does: 'Link that match (or pick that reason in the skip sheet)' },
  { keys: ['C'], does: 'Choose another product (search)' },
  { keys: ['N'], does: 'Add as a new product' },
  { keys: ['S'], does: 'Skip, then pick a reason' },
  { keys: ['H'], does: 'Ask for help' },
  { keys: ['→'], does: 'Not now' },
  { keys: ['U', '⌘Z'], does: 'Undo (for 5 seconds)' },
  { keys: ['O', 'P'], does: 'Open the page / our pick' },
  { keys: ['X', 'M', 'K'], does: 'Likely wrong: undo our link, merge, keep both' },
  { keys: ['↑', '↓', '↵'], does: 'In search: move to a result and link it' },
  { keys: ['⌘↵'], does: 'In the form: save' },
  { keys: ['Esc'], does: 'Close search, a sheet or the form' },
  { keys: ['?'], does: 'Show these keys' }
];

// The link shapes a tip treats differently; null for an ordinary product link.
export const getLinkShape = url => {
  const parsed = parseUrl(url);
  if (!parsed) return null;
  const { hostname, pathname, search } = parsed;
  if (/(^|\.)amazon\.[a-z.]+$/.test(hostname) || ['amzn.to', 'a.co', 'link.amazon'].includes(hostname)) return 'amazon';
  if (/\/_t\/c\/|^\/T\/v\d/.test(pathname) || /^(click|clicks|email|links?)\./.test(hostname)) return 'email';
  const isProductPath = /\/(p|product|products|dp|item|itm)\//i.test(pathname);
  if (
    /(^|\/)(search|searchresults|search-results|catalogsearch)(\/|$)/i.test(pathname) ||
    (!isProductPath && /[?&](q|query|search|searchTerm|keywords?)=/.test(search))
  ) {
    return 'search';
  }
  return /\/collections\/[^/]+\/?$/i.test(pathname) ? 'collection' : null;
};

const parseUrl = url => {
  try {
    return new URL(url);
  } catch {
    return null;
  }
};

const fill = (copy, link, options) => (typeof copy === 'function' ? copy(link, options) : copy);
